import React, { useState } from 'react';
import { Bell, BellRing, Check, Clock, AlertCircle, AlertTriangle, TrendingUp, Building2, MapPin, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import { useSiteAwareNotifications, NotificationScope } from '@/hooks/useSiteAwareNotifications';
import { useSiteAwareMatchingAlerts, MatchingAlert } from '@/hooks/useSiteAwareMatchingAlerts';
import { usePageSiteContext } from '@/contexts/PageSiteContext';

export const NotificationCenter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('notifications');
  const navigate = useNavigate();
  
  const { pageSiteId, pageSiteName } = usePageSiteContext();

  // LAZY-LOAD: Only run notification queries when the sheet is open
  const {
    notifications,
    context: notificationContext,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
  } = useSiteAwareNotifications({ 
    overrideSiteId: pageSiteId, 
    overrideSiteName: pageSiteName,
    enabled: isOpen, // Only fetch when panel is open
  });

  const {
    alerts: matchingAlerts,
    unviewedCount: unviewedAlertsCount,
    markAsViewed,
  } = useSiteAwareMatchingAlerts({ 
    overrideSiteId: pageSiteId, 
    overrideSiteName: pageSiteName,
    enabled: isOpen, // Only fetch when panel is open
  });

  const isAdmin = notificationContext.isAdmin;

  const handleAlertClick = (alert: MatchingAlert) => {
    markAsViewed(alert.id);
    setIsOpen(false);
    navigate('/admin/command-center');
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const getPriorityIcon = (priority: number) => {
    if (priority <= 2) return <AlertCircle className="h-4 w-4 text-destructive" />;
    if (priority === 3) return <Clock className="h-4 w-4 text-warning" />;
    return <Bell className="h-4 w-4 text-primary" />;
  };

  const getAlertPriorityIcon = (priority: string) => {
    if (priority === 'critical') return <AlertTriangle className="h-4 w-4 text-destructive" />;
    if (priority === 'high') return <AlertCircle className="h-4 w-4 text-warning" />;
    return <TrendingUp className="h-4 w-4 text-muted-foreground" />;
  };

  const getPriorityColor = (priority: number) => {
    if (priority <= 2) return 'border-l-destructive';
    if (priority === 3) return 'border-l-warning';
    return 'border-l-primary';
  };

  const getAlertPriorityColor = (priority: string) => {
    if (priority === 'critical') return 'border-l-destructive';
    if (priority === 'high') return 'border-l-warning';
    return 'border-l-muted-foreground';
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const getScopeIndicator = (scope: NotificationScope) => {
    switch (scope) {
      case 'admin':
        return { icon: <Globe className="h-3 w-3" />, label: 'Platform-wide notifications' };
      case 'organization':
        return { icon: <Building2 className="h-3 w-3" />, label: `${notificationContext.scopeName} notifications` };
      case 'trial_site':
        return { icon: <MapPin className="h-3 w-3" />, label: `${notificationContext.scopeName} notifications` };
      default:
        return { icon: <Bell className="h-3 w-3" />, label: 'Your personal notifications' };
    }
  };

  const scopeIndicator = getScopeIndicator(notificationContext.scope);
  // When closed, we don't have counts — show just the bell
  const totalUnread = isOpen ? (unreadCount + unviewedAlertsCount) : 0;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" className="relative">
          {totalUnread > 0 ? (
            <BellRing className="h-4 w-4" />
          ) : (
            <Bell className="h-4 w-4" />
          )}
          {totalUnread > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {totalUnread > 99 ? '99+' : totalUnread}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between">
            <span>Notifications</span>
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={handleMarkAllAsRead}>
                <Check className="h-4 w-4 mr-2" />
                Mark all read
              </Button>
            )}
          </SheetTitle>
          <SheetDescription className="flex items-center gap-2">
            {scopeIndicator.icon}
            <span>{scopeIndicator.label}</span>
          </SheetDescription>
        </SheetHeader>
        
        <Separator className="my-4" />
        
        {isAdmin ? (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="notifications" className="relative">
                Notifications
                {unreadCount > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 w-5 flex items-center justify-center p-0 text-xs">
                    {unreadCount}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="alerts" className="relative">
                Matching Alerts
                {unviewedAlertsCount > 0 && (
                  <Badge variant="destructive" className="ml-2 h-5 w-5 flex items-center justify-center p-0 text-xs">
                    {unviewedAlertsCount}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="notifications" className="mt-4">
              <ScrollArea className="h-[calc(100vh-200px)]">
                {renderNotificationsList()}
              </ScrollArea>
            </TabsContent>

            <TabsContent value="alerts" className="mt-4">
              <ScrollArea className="h-[calc(100vh-200px)]">
                {matchingAlerts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No new matching alerts</p>
                    <p className="text-sm">High-quality matches for this site will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {matchingAlerts.map((alert) => (
                      <Card 
                        key={alert.id}
                        className={`cursor-pointer border-l-4 ${getAlertPriorityColor(alert.alert_priority)} bg-muted/30 hover:bg-muted/50 transition-colors`}
                        onClick={() => handleAlertClick(alert)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {getAlertPriorityIcon(alert.alert_priority)}
                              <Badge variant={alert.alert_priority === 'critical' ? 'destructive' : 'secondary'} className="text-xs">
                                {alert.alert_priority.toUpperCase()} - {alert.match_score}%
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                              <span className="text-xs text-muted-foreground">
                                {formatTimeAgo(alert.created_at)}
                              </span>
                            </div>
                          </div>
                          <p className="text-sm font-medium">
                            {(alert.metadata as any)?.trial_title || 'Trial Match'}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Patient: {(alert.metadata as any)?.patient_initials || '??'} | 
                            NCT: {(alert.metadata as any)?.trial_nct_id || 'N/A'}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
        ) : (
          <ScrollArea className="h-[calc(100vh-120px)]">
            {renderNotificationsList()}
          </ScrollArea>
        )}
      </SheetContent>
    </Sheet>
  );

  function renderNotificationsList() {
    if (loading) {
      return (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-muted rounded w-full mb-1"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      );
    }

    if (notifications.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No notifications yet</p>
          <p className="text-sm">
            {notificationContext.scope === 'admin' 
              ? 'Platform-wide notifications will appear here'
              : notificationContext.scope === 'organization'
                ? `Notifications for ${notificationContext.scopeName} will appear here`
                : notificationContext.scope === 'trial_site'
                  ? `Notifications for ${notificationContext.scopeName} will appear here`
                  : "You'll see important updates here"}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {notifications.map((notification) => (
          <Card 
            key={notification.id}
            className={`cursor-pointer border-l-4 ${getPriorityColor(notification.priority)} ${
              !notification.read ? 'bg-muted/30' : ''
            } hover:bg-muted/50 transition-colors`}
            onClick={() => markAsRead(notification.id)}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  {getPriorityIcon(notification.priority)}
                  <h4 className={`text-sm font-medium ${!notification.read ? 'font-semibold' : ''}`}>
                    {notification.title}
                  </h4>
                </div>
                <div className="flex items-center gap-2">
                  {!notification.read && (
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {formatTimeAgo(notification.created_at)}
                  </span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {notification.content}
              </p>
              {notification.metadata?.trial_id && (
                <Badge variant="secondary" className="mt-2 text-xs">
                  Trial Update
                </Badge>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }
};
